// ==================== FORGOT PASSWORD PAGE ====================

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Lock,
  Eye,
  EyeOff,
  CheckCircle2, 
  AlertCircle, 
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  KeyRound,
  ShieldCheck
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { ROUTES } from '../../constants';
import { authService } from '../../services';
import { fadeInUp, staggerContainer } from '../../utils/animations';

// ==================== CONSTANTS ====================

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

// ==================== VALIDATION SCHEMAS ====================

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const passwordSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[@$!%*?&]/, 'Password must contain at least one special character (@$!%*?&)'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type EmailFormData = z.infer<typeof emailSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

// ==================== STEP INDICATOR ====================

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

const StepIndicator = ({ currentStep, totalSteps }: StepIndicatorProps) => {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div
          key={i}
          className={`h-2 rounded-full transition-all duration-300 ${
            i < currentStep 
              ? 'w-8 bg-(--color-signal-orange)' 
              : i === currentStep 
                ? 'w-8 bg-(--color-signal-orange)/50' 
                : 'w-2 bg-(--color-border-gray)'
          }`}
        />
      ))}
    </div>
  );
};

// ==================== SUCCESS VIEW ====================

const SuccessView = () => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="text-center py-8"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="w-20 h-20 mx-auto mb-6 rounded-full bg-(--color-success)/20 flex items-center justify-center"
      >
        <CheckCircle2 size={40} className="text-(--color-success)" />
      </motion.div>
      
      <h2 className="text-2xl font-heading font-bold text-white mb-3">
        Password Reset Successfully!
      </h2>
      
      <p className="text-(--color-soft-gray) mb-6 max-w-sm mx-auto">
        Your password has been changed. You can now sign in with your new password.
      </p>

      <Button 
        variant="primary" 
        rightIcon={<ArrowRight size={18} />}
        onClick={() => navigate(ROUTES.AUTH.LOGIN)}
      >
        Sign In Now
      </Button>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================

const ForgotPasswordPage = () => {
  // State
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Forms
  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first OTP input when entering step 2
  useEffect(() => {
    if (step === 2) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  // ==================== STEP 1: EMAIL SUBMISSION ====================

  const handleEmailSubmit = async (data: EmailFormData) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authService.forgotPassword(data.email);
      
      if (response.success) {
        setEmail(data.email);
        setResendCooldown(RESEND_COOLDOWN);
        setStep(2);
      } else {
        setError(response.message || 'Failed to send reset code. Please try again.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== STEP 2: OTP HANDLING ====================

  const handleOtpChange = useCallback((index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    
    // Handle multi-digit input (from paste via onChange)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, OTP_LENGTH);
      for (let i = 0; i < OTP_LENGTH; i++) {
        newOtp[i] = digits[i] || '';
      }
      setOtp(newOtp);
      if (digits.length >= OTP_LENGTH) {
        inputRefs.current[OTP_LENGTH - 1]?.focus();
      }
      return;
    }

    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [otp]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, OTP_LENGTH);
    
    if (digits.length > 0) {
      const newOtp = new Array(OTP_LENGTH).fill('');
      for (let i = 0; i < digits.length && i < OTP_LENGTH; i++) {
        newOtp[i] = digits[i];
      }
      setOtp(newOtp);
      const focusIndex = Math.min(digits.length, OTP_LENGTH) - 1;
      inputRefs.current[focusIndex]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [otp]);

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    
    if (otpCode.length !== OTP_LENGTH) {
      setError('Please enter the complete verification code');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await authService.verifyPasswordResetOtp(email, otpCode);
      
      if (response.success) {
        setStep(3);
      } else {
        setError(response.message || 'Invalid verification code. Please try again.');
        // Clear OTP inputs on error
        setOtp(new Array(OTP_LENGTH).fill(''));
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }
    } catch {
      setError('An error occurred. Please try again.');
      setOtp(new Array(OTP_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    setError(null);
    setIsResending(true);

    try {
      const response = await authService.resendPasswordResetOtp(email);
      
      if (response.success) {
        setResendCooldown(RESEND_COOLDOWN);
        setOtp(new Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      } else {
        setError(response.message || 'Failed to resend code. Please try again.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Auto-verify when all digits are entered
  useEffect(() => {
    const otpCode = otp.join('');
    if (otpCode.length === OTP_LENGTH && step === 2 && !isLoading) {
      handleVerifyOtp();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, step]);

  // ==================== STEP 3: PASSWORD RESET ====================

  const handlePasswordSubmit = async (data: PasswordFormData) => {
    const otpCode = otp.join('');
    
    setError(null);
    setIsLoading(true);

    try {
      const response = await authService.resetPassword(email, otpCode, data.password);
      
      if (response.success) {
        setIsSuccess(true);
      } else {
        // If OTP is invalid/expired, go back to step 2
        if (response.message?.toLowerCase().includes('otp') || 
            response.message?.toLowerCase().includes('expired') ||
            response.message?.toLowerCase().includes('invalid')) {
          setStep(2);
          setOtp(new Array(OTP_LENGTH).fill(''));
        }
        setError(response.message || 'Failed to reset password. Please try again.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== NAVIGATION ====================

  const handleBack = () => {
    setError(null);
    if (step === 2) {
      setStep(1);
      setOtp(new Array(OTP_LENGTH).fill(''));
    } else if (step === 3) {
      setStep(2);
    }
  };

  // ==================== RENDER ====================

  // Show success view
  if (isSuccess) {
    return <SuccessView />;
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="animate-fade-in"
    >
      {/* Step Indicator */}
      <StepIndicator currentStep={step} totalSteps={3} />

      {/* Header */}
      <motion.div variants={fadeInUp} className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center">
          {step === 1 && <KeyRound size={32} className="text-(--color-signal-orange)" />}
          {step === 2 && <ShieldCheck size={32} className="text-(--color-signal-orange)" />}
          {step === 3 && <Lock size={32} className="text-(--color-signal-orange)" />}
        </div>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-2">
          {step === 1 && 'Forgot Password?'}
          {step === 2 && 'Enter Verification Code'}
          {step === 3 && 'Create New Password'}
        </h1>
        <p className="text-(--color-muted-gray)">
          {step === 1 && "No worries! Enter your email and we'll send you a reset code."}
          {step === 2 && (
            <>
              We've sent a verification code to{' '}
              <span className="text-(--color-signal-orange)">{email}</span>
            </>
          )}
          {step === 3 && 'Enter your new password below.'}
        </p>
      </motion.div>

      {/* Error Message */}
      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-start gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/30 mb-6"
            role="alert"
          >
            <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Step 1: Email Form */}
          {step === 1 && (
            <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-5">
              <div>
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="Enter your email"
                  error={emailForm.formState.errors.email?.message}
                  leftIcon={<Mail size={18} />}
                  autoComplete="email"
                  {...emailForm.register('email')}
                />
              </div>

              <div>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  isLoading={isLoading}
                  rightIcon={<ArrowRight size={18} />}
                >
                  Send Reset Code
                </Button>
              </div>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <div>
              {/* OTP Input */}
              <div className="mb-6">
                <div className="flex justify-center gap-3">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={OTP_LENGTH}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      autoComplete="one-time-code"
                      className="w-12 h-14 text-center text-xl font-bold text-white bg-(--color-navy-light) 
                                 border-2 border-(--color-border-gray) rounded-lg 
                                 focus:border-(--color-signal-orange) focus:outline-none focus:ring-2 
                                 focus:ring-(--color-signal-orange)/20 transition-all"
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>

              {/* Continue Button */}
              <div>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={handleVerifyOtp}
                  disabled={otp.join('').length !== OTP_LENGTH}
                  rightIcon={<ArrowRight size={18} />}
                >
                  Continue
                </Button>
              </div>

              {/* Resend Code */}
              <div className="mt-6 text-center">
                <p className="text-sm text-(--color-muted-gray) mb-2">
                  Didn't receive the code?
                </p>
                <button
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isResending}
                  className="inline-flex items-center gap-2 text-sm text-(--color-signal-orange) 
                             hover:text-(--color-orange-light) font-medium transition-colors
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResending ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : resendCooldown > 0 ? (
                    <>Resend code in {resendCooldown}s</>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      Resend Code
                    </>
                  )}
                </button>
              </div>

              {/* Back Button */}
              <div className="mt-6">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-sm text-(--color-muted-gray) 
                             hover:text-white transition-colors mx-auto"
                >
                  <ArrowLeft size={16} />
                  Use a different email
                </button>
              </div>
            </div>
          )}

          {/* Step 3: New Password Form */}
          {step === 3 && (
            <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="space-y-5">
              <div>
                <Input
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  error={passwordForm.formState.errors.password?.message}
                  leftIcon={<Lock size={18} />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                  autoComplete="new-password"
                  {...passwordForm.register('password')}
                />
              </div>

              <div>
                <Input
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your new password"
                  error={passwordForm.formState.errors.confirmPassword?.message}
                  leftIcon={<Lock size={18} />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                  autoComplete="new-password"
                  {...passwordForm.register('confirmPassword')}
                />
              </div>

              {/* Password Requirements */}
              <div className="p-3 bg-(--color-navy-light)/50 rounded-lg border border-(--color-border-gray)">
                <p className="text-xs text-(--color-muted-gray) mb-2">Password must contain:</p>
                <ul className="text-xs text-(--color-muted-gray) space-y-1">
                  <li className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${passwordForm.watch('password')?.length >= 8 ? 'bg-(--color-success)' : 'bg-(--color-border-gray)'}`} />
                    At least 8 characters
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${/[A-Z]/.test(passwordForm.watch('password') || '') ? 'bg-(--color-success)' : 'bg-(--color-border-gray)'}`} />
                    One uppercase letter
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${/[a-z]/.test(passwordForm.watch('password') || '') ? 'bg-(--color-success)' : 'bg-(--color-border-gray)'}`} />
                    One lowercase letter
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${/[0-9]/.test(passwordForm.watch('password') || '') ? 'bg-(--color-success)' : 'bg-(--color-border-gray)'}`} />
                    One number
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${/[@$!%*?&]/.test(passwordForm.watch('password') || '') ? 'bg-(--color-success)' : 'bg-(--color-border-gray)'}`} />
                    One special character (@$!%*?&)
                  </li>
                </ul>
              </div>

              <div>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  isLoading={isLoading}
                  rightIcon={<ArrowRight size={18} />}
                >
                  Reset Password
                </Button>
              </div>

              {/* Back Button */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-2 text-sm text-(--color-muted-gray) 
                             hover:text-white transition-colors mx-auto"
                >
                  <ArrowLeft size={16} />
                  Back to verification
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Info Box */}
      {step === 1 && (
        <motion.div 
          variants={fadeInUp}
          className="mt-8 p-4 bg-(--color-navy-light)/50 rounded-lg border border-(--color-border-gray)"
        >
          <div className="flex items-start gap-3">
            <Mail size={20} className="text-(--color-signal-orange) shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-white mb-1">Check your email</p>
              <p className="text-xs text-(--color-muted-gray)">
                After submitting, you'll receive a 6-digit code to verify your identity 
                and reset your password.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Back to Login */}
      <motion.div variants={fadeInUp} className="mt-6 text-center">
        <Link
          to={ROUTES.AUTH.LOGIN}
          className="text-sm text-(--color-muted-gray) hover:text-white transition-colors"
        >
          ← Back to Sign In
        </Link>
      </motion.div>
    </motion.div>
  );
};

export { ForgotPasswordPage };

